import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, ConciergeBell, Loader2, RotateCcw, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { locale, tr } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("dc_token")}` });
const eventValue = (value) => {
  const translateOne = (item) => {
    const raw = String(item);
    return ["yes", "no"].includes(raw.toLowerCase()) ? tr(raw.toUpperCase()) : tr(raw);
  };
  return Array.isArray(value) ? value.map(translateOne).join(", ") : translateOne(value);
};

export function LucasAssistant({ open, setOpen, user, onRequestCreated, onOpenRequest }) {
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);

  const loadSession = async () => {
    try {
      const { data } = await axios.get(`${API}/portal/assistant/session`, { headers: authHeader() });
      setSession(data);
      setMessages(data.messages || []);
    } catch {
      setError(tr("Lucas is temporarily unavailable"));
    }
  };

  useEffect(() => {
    if (open && !session) loadSession();
  }, [open, session]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, session?.ready_for_review]);

  const reviewRows = useMemo(() => {
    if (!session?.ready_for_review || !session.schema) return [];
    const rows = session.schema.base_fields.map((field) => [field.label, session.base?.[field.key]]);
    rows.push(...session.schema.fields.map((field) => [field.label, session.requirements?.[field.key]]));
    return rows.filter(([, value]) => value !== "" && value !== null && value !== undefined && (!Array.isArray(value) || value.length));
  }, [session]);

  const readEvents = async (response) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let assistantText = "";
    setMessages((current) => [...current, { role: "assistant", content: "", pending: true }]);
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split("\n\n");
      buffer = events.pop() || "";
      for (const eventBlock of events) {
        const event = eventBlock.match(/^event: (.+)$/m)?.[1];
        const dataLine = eventBlock.match(/^data: ([\s\S]+)$/m)?.[1];
        if (!event || !dataLine) continue;
        const payload = JSON.parse(dataLine);
        if (event === "token") {
          assistantText += payload.content;
          setMessages((current) => current.map((m, index) => index === current.length - 1 ? { ...m, content: assistantText, pending: true } : m));
        }
        if (event === "state") {
          setSession(payload);
          setMessages((current) => current.map((m, index) => index === current.length - 1 ? { ...m, pending: false } : m));
        }
        if (event === "error") throw new Error(payload.detail || tr("Lucas could not respond right now"));
      }
    }
  };

  const send = async (text = input) => {
    const message = text.trim();
    if (!message || streaming || !session) return;
    setInput("");
    setError("");
    setStreaming(true);
    const userMessage = { role: "user", content: message, created_at: new Date().toISOString() };
    setMessages((current) => [...current, userMessage]);
    try {
      const response = await fetch(`${API}/portal/assistant/message`, {
        method: "POST",
        headers: { ...authHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session.session_id, message, language: locale() }),
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail.detail || tr("Lucas could not respond right now"));
      }
      await readEvents(response);
    } catch (err) {
      setMessages((current) => current.filter((m) => !m.pending));
      setError(err.message);
    } finally {
      setStreaming(false);
    }
  };

  const confirm = async () => {
    if (!session?.ready_for_review || confirming) return;
    setConfirming(true);
    try {
      const { data } = await axios.post(`${API}/portal/assistant/confirm`, { session_id: session.session_id, language: locale() }, { headers: authHeader() });
      toast.success(tr("Your request has been received"), { description: tr("Lucas Grant has passed your details to your advisor.") });
      setSession((current) => ({ ...current, status: "submitted", ready_for_review: false, request_id: data.request.id }));
      setMessages((current) => [...current, { role: "assistant", content: tr("Thank you. Your enquiry has been submitted and will now appear in Your Requests.") }]);
      onRequestCreated?.(data.request);
      if (data.document_note) toast(tr("Document upload available"), { description: tr("Open View Request to attach the supporting document.") });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : tr("Lucas could not submit this enquiry yet"));
    } finally {
      setConfirming(false);
    }
  };

  const reset = async () => {
    if (!session || streaming) return;
    const { data } = await axios.post(`${API}/portal/assistant/reset`, { session_id: session.session_id, language: locale() }, { headers: authHeader() });
    setSession(data);
    setMessages([]);
    setError("");
  };

  const greetingName = user?.name?.split(" ")[0] || tr("Member");

  const closePanel = () => {
    setOpen(false);
    setSession(null);
    setMessages([]);
    setError("");
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          data-testid="lucas-open-button"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[65] flex items-center gap-3 bg-gold text-black px-5 py-4 text-[0.62rem] uppercase tracking-[0.25em] shadow-[0_18px_50px_rgba(198,169,98,0.28)] hover:bg-gold-bright transition-colors duration-300"
        >
          <ConciergeBell strokeWidth={1.5} size={18} />
          {tr("Lucas Grant")}
        </button>
      )}
      <AnimatePresence>
        {open && (
          <motion.aside
            data-testid="lucas-panel"
            className="fixed bottom-24 right-6 z-[70] w-[calc(100vw-3rem)] max-w-[430px] h-[min(680px,calc(100vh-8rem))] bg-[#09090A]/95 backdrop-blur-xl border border-gold/25 shadow-[0_30px_100px_rgba(0,0,0,0.7)] flex flex-col overflow-hidden"
            initial={{ opacity: 0, y: 28, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="p-5 border-b border-white/10 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full border border-gold/45 bg-gold/10 flex items-center justify-center text-gold font-serif text-lg">LG</div>
              <div className="flex-1">
                <p className="font-serif text-xl font-light" data-testid="lucas-title">{tr("Lucas Grant")}</p>
                <p className="text-[0.58rem] uppercase tracking-[0.25em] text-gold/80 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {tr("Private Assistant")}
                </p>
              </div>
              <button type="button" data-testid="lucas-reset-button" onClick={reset} className="text-white/35 hover:text-gold transition-colors" aria-label={tr("New conversation")}>
                <RotateCcw strokeWidth={1.5} size={17} />
              </button>
              <button type="button" data-testid="lucas-close-button" onClick={closePanel} className="text-white/45 hover:text-gold transition-colors" aria-label={tr("Close Lucas Grant")}>
                <X strokeWidth={1.5} size={20} />
              </button>
            </div>

            <div ref={scrollRef} className="modal-scroll flex-1 overflow-y-auto p-5 space-y-4" data-testid="lucas-messages">
              {messages.length === 0 && (
                <div className="border border-white/10 bg-white/[0.03] p-5">
                  <p className="text-sm font-light leading-relaxed text-white/78">
                    {tr("Good day, {{name}}. I’m Lucas Grant, your private assistant. Tell me what you would like arranged, and I’ll gather the details with care before anything is submitted.", { name: greetingName })}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {[tr("Arrange a chauffeur"), tr("Plan a journey"), tr("Reserve fine dining")].map((prompt) => (
                      <button key={prompt} type="button" data-testid={`lucas-prompt-${prompt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => send(prompt)} className="border border-gold/25 text-gold/85 text-[0.58rem] uppercase tracking-[0.18em] px-3 py-2 hover:border-gold/60 transition-colors">
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((message, index) => (
                <div key={`${message.created_at || index}-${index}`} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[86%] p-4 text-sm font-light leading-relaxed ${message.role === "user" ? "bg-gold text-black" : "border border-white/10 bg-white/[0.04] text-white/82"}`} data-testid={`lucas-message-${message.role}`}>
                    {message.content}
                    {message.pending && <Loader2 className="inline-block ml-2 animate-spin" size={13} />}
                  </div>
                </div>
              ))}
              {session?.ready_for_review && (
                <div className="border border-gold/35 bg-gold/[0.06] p-5" data-testid="lucas-review-card">
                  <p className="text-[0.62rem] uppercase tracking-[0.28em] text-gold mb-3">{tr("Review & Confirm")}</p>
                  <p className="font-serif text-xl font-light mb-4">{tr(session.service)}</p>
                  <div className="space-y-3 max-h-56 overflow-y-auto pr-1">
                    {reviewRows.map(([label, value]) => (
                      <p key={label} className="text-xs font-light">
                        <span className="block uppercase tracking-[0.2em] text-white/38 mb-1">{tr(label)}</span>
                        <span className="text-white/82">{eventValue(value)}</span>
                      </p>
                    ))}
                  </div>
                  {session.schema?.document_note && <p className="text-gold/80 text-xs font-light mt-4">{tr(session.schema.document_note)}</p>}
                  <button type="button" data-testid="lucas-confirm-button" onClick={confirm} disabled={confirming} className="mt-5 w-full bg-gold text-black py-3.5 text-[0.62rem] uppercase tracking-[0.28em] hover:bg-gold-bright transition-colors disabled:opacity-60">
                    {confirming ? tr("Submitting…") : tr("Confirm & Submit Enquiry")}
                  </button>
                </div>
              )}
              {session?.status === "submitted" && (
                <button type="button" data-testid="lucas-view-request-button" onClick={() => onOpenRequest?.(session.request_id)} className="w-full border border-gold/40 text-gold py-3 text-[0.62rem] uppercase tracking-[0.25em] hover:bg-gold/10 transition-colors">
                  {tr("View Submitted Request")}
                </button>
              )}
              {error && <p data-testid="lucas-error" className="text-red-400/90 text-xs text-center">{error}</p>}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-4 border-t border-white/10 flex items-center gap-3">
              <input
                data-testid="lucas-message-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={tr("Tell Lucas what you would like arranged…")}
                className="flex-1 bg-white/[0.04] border border-white/10 px-4 py-3 text-sm font-light outline-none focus:border-gold/50"
                disabled={streaming}
              />
              <button type="submit" data-testid="lucas-send-button" disabled={!input.trim() || streaming} className="w-11 h-11 bg-gold text-black flex items-center justify-center hover:bg-gold-bright transition-colors disabled:opacity-50" aria-label={tr("Send message")}>
                {streaming ? <Loader2 className="animate-spin" size={17} /> : <ArrowUp strokeWidth={1.75} size={18} />}
              </button>
            </form>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
